import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import type { League, FixtureStatus } from '@shootout-comps/types';
import { isSameLocalDate } from '../../utils/fixtureDate';

interface DivisionFixturesByDatePageProps {
  leagueId: string;
  divisionId: string;
  date: string; // YYYY-MM-DD, local calendar day — see utils/fixtureDate.ts
  onBack: () => void;
  onOpenFixture: (fixtureId: string) => void;
}

export default function DivisionFixturesByDatePage({ leagueId, divisionId, date, onBack, onOpenFixture }: DivisionFixturesByDatePageProps) {
  const { fetchLeague } = useLeague();
  const { t } = useLang();
  const STATUS_LABEL: Record<FixtureStatus, { label: string; color: string }> = {
    scheduled: { label: t.fixtureStatusScheduled, color: 'var(--chalk-dim)' },
    lineup_set: { label: t.fixtureStatusLineupSet, color: '#3498db' },
    in_progress: { label: t.fixtureStatusInProgress, color: '#2ecc71' },
    complete: { label: t.complete, color: 'var(--gold)' },
  };
  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchLeague, leagueId]);

  useEffect(reload, [reload]);

  const division = league?.divisions.find(d => d._id === divisionId);

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  const dayFixtures = division.fixtures.filter(f => f.scheduledAt && isSameLocalDate(f.scheduledAt, date));
  // Parsed from the YYYY-MM-DD param as a local date, purely for the heading — never sent
  // anywhere, so the UTC-vs-local distinction that matters for grouping doesn't apply here.
  const [y, m, d] = date.split('-').map(Number);
  const dateLabel = new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← Fixtures
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)', marginBottom: '0.25rem' }}>
        {dateLabel}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1.5rem' }}>
        {division.name} · {dayFixtures.length} fixture{dayFixtures.length !== 1 ? 's' : ''}
      </p>

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      {dayFixtures.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '3rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '2px dashed rgba(255,255,255,0.1)' }}>
          No fixtures scheduled on this date.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {dayFixtures.map(f => {
            const status = STATUS_LABEL[f.status];
            return (
              <div
                key={f._id}
                onClick={() => onOpenFixture(f._id)}
                style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.9rem 1.1rem', cursor: 'pointer' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                    {f.homeTeamName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>vs</span> {f.awayTeamName}
                    {f.status === 'complete' && (
                      <span style={{ color: 'var(--gold)', fontWeight: 700, marginLeft: '0.5rem' }}>{f.homeFrameWins}–{f.awayFrameWins}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                    {f.scheduledAt ? new Date(f.scheduledAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}{f.venue ? ` · ${f.venue}` : ''}
                  </div>
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: status.color, whiteSpace: 'nowrap' }}>{status.label}</span>
                <span style={{ color: 'var(--chalk-dim)' }}>›</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
